import {Action, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {PMonitorTaskModule} from '@/views/system/modules/interfaces';
import {PModuleType} from '@/enums/configs';
import {
  PMProcessActionType,
  PMProcessStatus,
  PMProcessSystemType,
} from '@/views/system/modules/enums';
import {EntityModule} from '../entityModule';
import {httpClient} from '@/api/baseApi';
import {getEventData, getEventTypes, getLastEvents} from './base';

/*
    Динамический модуль хранилища vuex для работы с задачей определения состояния по датчикам
*/

const moduleName = 'pmonitorStatesTask';

function transformGet(elem: any): PMonitorTaskModule {
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    type: PModuleType.MonitorStatesTasks,
    description: elem.description,
    processStatus: elem.process_status as PMProcessStatus,
    processStatusDescription: elem.process_status_description,
    status: elem.status,
    nodes: elem.nodes,
    alias: elem.alias,
    errorCode: elem.error_code,
    states: elem.states,
    child: elem.child,
    boundaryModules: elem.boundaryModules,
    maxMessageCount: elem.max_message_count,
    notifyHttpType: elem.notify_http_type,
    notifyHttpUri: elem.notify_http_uri,
    notifyHttpMessage: elem.notify_http_message,
  };
}

function transformGetById(elem: any): PMonitorTaskModule {
  return transformGet(elem);
}

function transformCreateEdit(elem: any): PMonitorTaskModule {
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    alias: elem.alias,
    type: PModuleType.MonitorStatesTasks,
    description: elem.description,
    nodes: elem.nodes,
    errorCode: elem.error_code,
    states: elem.states,
    child: elem.child,
    boundaryModules: elem.boundary_modules,
    maxMessageCount: elem.max_message_count,
    notifyHttpType: elem.notify_http_type,
    notifyHttpUri: elem.notify_http_uri,
    notifyHttpMessage: elem.notify_http_message,
  };
}

function transformSave(model: PMonitorTaskModule): any {
  return {
    id: model.id,
    name: model.name,
    description: model.description,
    nodes: model.nodes,
    error_code: model.errorCode,
    states: model.states,
    child: model.child,
    boundary_modules: model.boundaryModules,
    max_message_count: model.maxMessageCount,
    notify_http_type: model.notifyHttpType,
    notify_http_uri: model.notifyHttpUri,
    notify_http_message: model.notifyHttpMessage,
  };
}

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class PMonitorStatesTask extends EntityModule {
  protected routeName = 'monitor-states-tasks';

  get all(): PMonitorTaskModule[] {
    return super.all as PMonitorTaskModule[];
  }

  //Получение списка объектов с сервера
  @Action
  public async fetchAll() {
    return super.fetchAll(transformGet);
  }

  //Получение модели для создания объекта
  @Action
  public async create(): Promise<{data: PMonitorTaskModule}> {
    return super.create(transformCreateEdit) as Promise<{data: PMonitorTaskModule}>;
  }

  //Получение модели для редактирования объекта
  @Action
  public async edit(id: any): Promise<{data: PMonitorTaskModule}> {
    return super.edit(id, transformCreateEdit) as Promise<{data: PMonitorTaskModule}>;
  }

  //Создание/реактирование объекта
  @Action
  public async save(model: PMonitorTaskModule) {
    return super.save(model, transformSave);
  }

  //Получение объекта по идентификатору
  @Action
  async getById(id: any): Promise<{data: PMonitorTaskModule}> {
    return super.getById(id, transformGetById) as Promise<{data: PMonitorTaskModule}>;
  }

  @Action
  async processAction(params: {id: number; action: PMProcessActionType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/`, {
      action: params.action,
    });
    return data;
  }

  // Выполнить системную команду программного модуля
  @Action
  async processSystem(params: {id: number; action: PMProcessSystemType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/system`, {
      action: params.action,
    });
    return data;
  }

  //Получение типов событий
  @Action
  async getEventTypes(): Promise<any> {
    try {
      return await getEventTypes(this.routeName);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение списка последних событий
  @Action
  async getLastEvents(params: {id: number; eventType: string; limit?: number}): Promise<any> {
    try {
      return await getLastEvents(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение данных события
  @Action
  async getEventData(params: {id: number; eventType: string; timestamp: number}): Promise<any> {
    try {
      return await getEventData(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

export default getModule(PMonitorStatesTask);
